import { promises as fs } from "node:fs";
import { createHash, randomUUID } from "node:crypto";
import * as path from "node:path";
import type { Logger } from "pino";

import {
  MAX_UPLOAD_BYTES_PER_FILE,
  MAX_UPLOAD_BYTES_PER_MESSAGE,
  UploadSizeError,
} from "./session-upload-store.js";

/**
 * Per-workspace storage for chat-attached images. Mirrors the file-upload
 * store conceptually but with two important differences:
 *
 *   1. The agent (Claude/Codex/OpenCode) never reads images by path. Images
 *      are embedded as base64 multimodal blocks in the LLM prompt. So the
 *      on-disk location is purely a filesystem-organization concern, not a
 *      permissions one — but we still keep them inside the workspace so the
 *      existing `.gitignore` rule (`.appostle/`) covers them and cleanup
 *      tracks the project lifecycle.
 *
 *   2. Filenames are content-addressed (`<unix_ms>-<sha256_16>.<ext>`). The
 *      timestamp prefix gives natural sort order; the hash is collision-
 *      resistant. Real dedup-on-write would require a lookup table; we defer
 *      that. Identical content sent twice currently produces two files with
 *      identical hashes but different timestamps — a non-issue for v1.
 *
 * Why no manifest? Files have a manifest because the "View uploaded files"
 * UI lists them per agent. Images have no analogous UI — they're inline in
 * chat. The timeline carries the only index that matters: the user_message
 * row's image echo holds the workspace-relative `relativePath`, and clients
 * ask the daemon to read by path. GC strategy is deferred (tracked in the
 * plan as a follow-up).
 */

const IMAGES_DIR_REL = path.posix.join(".appostle", "tmp", "uploaded_images");
const GITIGNORE_MARKER_LINE = "# Added by Appostle: uploaded chat attachments";
const GITIGNORE_BLOCK = `${GITIGNORE_MARKER_LINE}\n.appostle/\n`;

const EXTENSION_BY_MIME: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
  "image/tiff": "tiff",
  "image/bmp": "bmp",
  "image/svg+xml": "svg",
};

function extensionForImage(input: { mimeType: string; fileName?: string | null }): string {
  // Prefer the user's filename extension if present — preserves intent for
  // formats with multiple legitimate extensions (jpg/jpeg, tif/tiff).
  if (input.fileName) {
    const dot = input.fileName.lastIndexOf(".");
    if (dot >= 0 && dot < input.fileName.length - 1) {
      const ext = input.fileName.slice(dot + 1).toLowerCase();
      // Sanity-cap to avoid filenames like ".this_is_not_an_extension".
      if (/^[a-z0-9]{1,8}$/.test(ext)) {
        return ext;
      }
    }
  }
  return EXTENSION_BY_MIME[input.mimeType] ?? "img";
}

function mimeTypeForExtension(ext: string): string {
  switch (ext) {
    case "png":
      return "image/png";
    case "jpg":
    case "jpeg":
      return "image/jpeg";
    case "gif":
      return "image/gif";
    case "webp":
      return "image/webp";
    case "avif":
      return "image/avif";
    case "heic":
      return "image/heic";
    case "heif":
      return "image/heif";
    case "tiff":
      return "image/tiff";
    case "bmp":
      return "image/bmp";
    case "svg":
      return "image/svg+xml";
    default:
      return "application/octet-stream";
  }
}

/**
 * Daemon-internal record returned by `persistImages`. Not exposed on the wire
 * directly — Phase 2 plumbs only the `id` + `relativePath` + a few metadata
 * fields onto the user_message timeline echo. Adding more fields here is
 * non-breaking since nothing serializes the whole object.
 */
export interface SessionImage {
  /** Caller-supplied id (when present); otherwise a fresh UUID. */
  id: string;
  fileName: string | null;
  mimeType: string;
  size: number;
  /** ISO-8601 UTC. */
  createdAt: string;
  messageId: string | null;
  /** Workspace-relative POSIX path, e.g. ".appostle/tmp/uploaded_images/1640-abc.png". */
  relativePath: string;
  /** sha256 hex (16 chars) of the bytes — useful for debugging and dedup. */
  hash: string;
}

/** Shape accepted by `persistImages`. Mirrors the wire `ImageAttachmentSchema`. */
export interface IncomingImage {
  data: string;
  mimeType: string;
  id?: string;
  fileName?: string | null;
  byteSize?: number;
}

export interface PersistImagesParams {
  cwd: string;
  agentId: string;
  messageId: string | null;
  images: readonly IncomingImage[];
}

export interface ReadImageParams {
  cwd: string;
  relativePath: string;
}

/**
 * Public listing entry returned by `listImages`. Matches the shape used by
 * the in-app "Images" tab: enough to render a thumbnail card and key it for
 * delete. We don't have a manifest for images, so `fileName` is the on-disk
 * basename (`<ms>-<hash>.<ext>`) rather than the user's original name — the
 * original is only preserved on the timeline echo.
 */
export interface SessionImageEntry {
  id: string;
  fileName: string;
  mimeType: string;
  size: number;
  createdAt: string;
  relativePath: string;
}

export class SessionImageStore {
  private readonly logger: Logger | undefined;

  constructor(opts: { logger?: Logger } = {}) {
    this.logger = opts.logger;
  }

  /**
   * Decode and write each image. Throws `UploadSizeError` on cap violations.
   * On success returns one entry per input image (in order).
   */
  async persistImages(params: PersistImagesParams): Promise<SessionImage[]> {
    const { cwd, agentId, messageId, images } = params;
    if (images.length === 0) {
      return [];
    }

    // Decode upfront to enforce size caps before touching the filesystem.
    const decoded: { image: IncomingImage; bytes: Buffer; hash: string }[] = [];
    let totalBytes = 0;
    for (const image of images) {
      let bytes: Buffer;
      try {
        bytes = Buffer.from(image.data, "base64");
      } catch (error) {
        throw new Error(
          `Failed to decode base64 image: ${
            error instanceof Error ? error.message : String(error)
          }`,
        );
      }
      if (bytes.length > MAX_UPLOAD_BYTES_PER_FILE) {
        throw new UploadSizeError(bytes.length, MAX_UPLOAD_BYTES_PER_FILE);
      }
      totalBytes += bytes.length;
      if (totalBytes > MAX_UPLOAD_BYTES_PER_MESSAGE) {
        throw new UploadSizeError(totalBytes, MAX_UPLOAD_BYTES_PER_MESSAGE);
      }
      const hash = createHash("sha256").update(bytes).digest("hex").slice(0, 16);
      decoded.push({ image, bytes, hash });
    }

    const dir = path.join(cwd, IMAGES_DIR_REL);
    await fs.mkdir(dir, { recursive: true });
    await this.ensureGitignoreEntry(cwd);

    const created: SessionImage[] = [];
    const createdAt = new Date().toISOString();
    const timestampMs = Date.now();

    for (const entry of decoded) {
      const ext = extensionForImage({
        mimeType: entry.image.mimeType,
        fileName: entry.image.fileName,
      });
      const baseName = `${timestampMs}-${entry.hash}.${ext}`;
      const relPath = path.posix.join(IMAGES_DIR_REL, baseName);
      const absPath = path.join(cwd, relPath);

      // Always re-write rather than checking existence: a corrupt or partial
      // file from an earlier interrupted write is safer to overwrite than skip.
      await fs.writeFile(absPath, entry.bytes);

      created.push({
        id: entry.image.id ?? randomUUID(),
        fileName: entry.image.fileName ?? null,
        mimeType: entry.image.mimeType,
        size: entry.bytes.length,
        createdAt,
        messageId,
        relativePath: relPath,
        hash: entry.hash,
      });
    }

    this.logger?.info(
      { agentId, count: created.length, totalBytes },
      "Persisted chat image uploads",
    );
    return created;
  }

  /**
   * Read bytes back from disk by workspace-relative path. Used by the (future
   * Phase 4) fetch-bytes RPC and by Phase 3's prompt-build path.
   *
   * Defense in depth: refuses anything outside `.appostle/tmp/uploaded_images/`
   * or anything containing `..`. The caller is the trust boundary, but
   * costing nothing to double-check at the I/O edge.
   */
  async readImageBytes(
    params: ReadImageParams,
  ): Promise<{ data: Buffer; mimeType: string; byteSize: number }> {
    const safe = path.posix.normalize(params.relativePath);
    if (!safe.startsWith(`${IMAGES_DIR_REL}/`) || safe.includes("..")) {
      throw new Error(`Refused to read image outside ${IMAGES_DIR_REL}: ${params.relativePath}`);
    }
    const abs = path.join(params.cwd, safe);
    const data = await fs.readFile(abs);
    const ext = path.extname(safe).slice(1).toLowerCase();
    return { data, mimeType: mimeTypeForExtension(ext), byteSize: data.length };
  }

  /**
   * List every image stored for this workspace. There is no manifest — the
   * directory listing IS the source of truth. Filenames carry the timestamp
   * (`<unix_ms>-<hash>.<ext>`); we parse that for `createdAt` and fall back to
   * file mtime if the prefix is missing.
   *
   * Returns newest-first to match the upload-store ordering expected by the UI.
   */
  async listImages(params: { cwd: string }): Promise<SessionImageEntry[]> {
    const dir = path.join(params.cwd, IMAGES_DIR_REL);
    let names: string[];
    try {
      names = await fs.readdir(dir);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") {
        return [];
      }
      throw error;
    }

    const entries: SessionImageEntry[] = [];
    for (const name of names) {
      // Skip dotfiles and the rare temp-rename artefact.
      if (name.startsWith(".")) continue;
      const abs = path.join(dir, name);
      let stat: import("node:fs").Stats;
      try {
        stat = await fs.stat(abs);
      } catch {
        continue;
      }
      if (!stat.isFile()) continue;
      const ext = path.extname(name).slice(1).toLowerCase();
      const mimeType = mimeTypeForExtension(ext);
      // Parse `<ms>-<hash>.<ext>` for the original write timestamp; fall back
      // to mtime if the prefix isn't a number.
      let createdAt: string;
      const dash = name.indexOf("-");
      const msCandidate = dash > 0 ? Number(name.slice(0, dash)) : Number.NaN;
      if (Number.isFinite(msCandidate) && msCandidate > 0) {
        createdAt = new Date(msCandidate).toISOString();
      } else {
        createdAt = stat.mtime.toISOString();
      }
      const relPath = path.posix.join(IMAGES_DIR_REL, name);
      entries.push({
        id: relPath,
        fileName: name,
        mimeType,
        size: stat.size,
        createdAt,
        relativePath: relPath,
      });
    }

    entries.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
    return entries;
  }

  /**
   * Delete a single stored image by workspace-relative path. Same traversal
   * guard as `readImageBytes`. Idempotent: returns `{ deleted: false }` if
   * the file is already gone (so the caller can refresh its listing).
   */
  async deleteImage(params: { cwd: string; relativePath: string }): Promise<{ deleted: boolean }> {
    const safe = path.posix.normalize(params.relativePath);
    if (!safe.startsWith(`${IMAGES_DIR_REL}/`) || safe.includes("..")) {
      throw new Error(`Refused to delete image outside ${IMAGES_DIR_REL}: ${params.relativePath}`);
    }
    const abs = path.join(params.cwd, safe);
    try {
      await fs.unlink(abs);
      return { deleted: true };
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") {
        return { deleted: false };
      }
      throw error;
    }
  }

  private async ensureGitignoreEntry(cwd: string): Promise<void> {
    const file = path.join(cwd, ".gitignore");
    let existing = "";
    try {
      existing = await fs.readFile(file, "utf8");
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") {
        throw error;
      }
    }
    if (existing.includes(GITIGNORE_MARKER_LINE)) {
      return;
    }
    const sep = existing.length === 0 || existing.endsWith("\n") ? "" : "\n";
    await fs.writeFile(file, `${existing}${sep}${GITIGNORE_BLOCK}`, "utf8");
  }
}
